const SETUP_GUIDE_DISMISSED_KEY = 'omnifill_setup_guide_dismissed';

const setupGuideStatus = (loading, done, blocked = false) => {
  if (loading) return 'loading';
  if (done) return 'done';
  return blocked ? 'waiting' : 'ready';
};

const buildSetupGuideSteps = ({
  orders = [],
  products = [],
  rules = [],
  ordersLoading = false,
  productsLoading = false,
  rulesLoading = false,
} = {}) => {
  const orderCount = ordersLoading ? null : orders.length;
  const productCount = productsLoading ? null : products.length;
  const unmappedOrders = ordersLoading ? null : orders.filter(orderNeedsProductDefinition).length;
  const exportRuleCount = rulesLoading ? null : rules.length;
  const routedOrders = ordersLoading ? null : orders.filter(order => ['routed', 'fulfilled'].includes(order.status)).length;

  return [
    {
      key: 'source',
      title: 'Bring in your first order',
      detail: 'Connect a live source from Sources, upload a campaign CSV, or add one manual order from Orders to test the workflow.',
      result: orderCount === null ? null : orderCount > 0 ? `${orderCount} imported` : 'No orders yet',
      Icon: Upload,
      status: setupGuideStatus(orderCount === null, orderCount > 0),
      action: 'Open Sources',
      screen: 'sources',
      focus: 'connect-source',
      secondaryAction: 'Add manual order',
      secondaryScreen: 'orders',
      secondaryFocus: 'manual-order',
    },
    {
      key: 'products',
      title: 'Create warehouse products',
      detail: 'Add each shippable product your warehouse recognizes, including SKUs. Mark digital or non-fulfillment items as skipped.',
      result: productCount === null ? null : productCount > 0 ? `${productCount} created` : 'No products yet',
      Icon: Inbox,
      status: setupGuideStatus(productCount === null, productCount > 0, orderCount === 0),
      action: 'Open Products',
      screen: 'products',
      focus: 'new-product',
    },
    {
      key: 'mapping',
      title: 'Map source item names',
      detail: 'Assign incoming item names to products. Use bundles when one purchased item should become multiple warehouse items.',
      result: unmappedOrders === null ? null : orderCount === 0 ? 'Waiting for orders' : unmappedOrders === 0 ? 'All assigned' : `${unmappedOrders} unassigned`,
      Icon: CheckCircle2,
      status: setupGuideStatus(unmappedOrders === null, orderCount > 0 && unmappedOrders === 0, orderCount === 0),
      action: 'Map products',
      screen: 'orders',
      focus: 'map-products',
    },
    {
      key: 'rule',
      title: 'Create a routing rule',
      detail: 'Create one export rule per warehouse or 3PL. Match by product, destination region, and source.',
      result: exportRuleCount === null ? null : exportRuleCount > 0 ? `${exportRuleCount} rule${exportRuleCount === 1 ? '' : 's'}` : 'No rules yet',
      Icon: SlidersHorizontal,
      status: setupGuideStatus(exportRuleCount === null, exportRuleCount > 0, productCount === 0),
      action: 'Set up routing',
      screen: 'export',
      focus: 'new-rule',
    },
    {
      key: 'handoff',
      title: 'Run one safe fulfillment handoff',
      detail: 'Download a CSV first, or configure SFTP, ShipBob, or DCIx on the rule. Start with a small match set.',
      result: routedOrders === null ? null : routedOrders > 0 ? `${routedOrders} routed` : 'Not routed yet',
      Icon: Truck,
      status: setupGuideStatus(routedOrders === null, routedOrders > 0, exportRuleCount === 0),
      action: 'Open Export',
      screen: 'export',
      focus: 'send-batch',
    },
    {
      key: 'verify',
      title: 'Check rule activity and order status',
      detail: 'Confirm the handoff from the rule activity panel on Export. Accepted orders should move to Routed; failed orders should remain available to retry.',
      result: routedOrders === null ? null : routedOrders > 0 ? 'Ready to verify' : 'Waiting for handoff',
      Icon: Clock3,
      status: setupGuideStatus(routedOrders === null, routedOrders > 0, routedOrders === 0),
      action: 'Open Export',
      screen: 'export',
      focus: 'view-activity',
    },
  ];
};

const setupGuideProgressFromSteps = steps => ({
  done: steps.filter(step => step.status === 'done').length,
  total: steps.length,
  loading: steps.some(step => step.status === 'loading'),
});

const SetupGuideScreen = ({
  orders = [],
  products = [],
  rules = [],
  ordersLoading = false,
  productsLoading = false,
  rulesLoading = false,
  onNavigate,
  onDismiss,
}) => {
  const steps = buildSetupGuideSteps({ orders, products, rules, ordersLoading, productsLoading, rulesLoading });
  const progress = setupGuideProgressFromSteps(steps);
  const progressPct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  const startGuideAction = (screen, focus) => {
    try {
      if (focus) {
        sessionStorage.setItem('omnifill_onboarding_focus', JSON.stringify({
          screen,
          focus,
          createdAt: Date.now(),
        }));
      }
    } catch {}
    onNavigate?.(screen);
  };

  const StepStatus = ({ status }) => {
    if (status === 'loading') return <SkeletonPill width={64} height={22}/>;
    if (status === 'done') return <span className="setup-step-status setup-step-status-done"><Check size={12}/>Done</span>;
    if (status === 'waiting') return <span className="setup-step-status">Waiting</span>;
    return <span className="setup-step-status setup-step-status-next">Next</span>;
  };

  return (
    <div className="dash-page-scroll setup-page">
      <div className="dash-panel setup-guide-panel">
        <div className="dash-panel-header setup-guide-header">
          <div style={{ minWidth:0 }}>
            <h1 className="dash-page-title">Setup Guide</h1>
            <p className="dash-page-subtitle" style={{ maxWidth:760 }}>Follow these steps in order to import orders, define products, route them to the right fulfillment partner, and verify the first handoff.</p>
          </div>
          <button onClick={onDismiss} title="Dismiss setup guide" style={{ width:32, height:32, border:'1px solid #E5E7EB', background:'#fff', color:'#6B7280', borderRadius:8, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <X size={15} strokeWidth={2.1}/>
          </button>
        </div>

        <div className="setup-guide-progress">
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:7 }}>
            <div style={{ fontSize:12, color:'#374151', fontWeight:900 }}>Progress</div>
            {progress.loading
              ? <SkeletonPill width={36} height={14}/>
              : <div style={{ fontSize:12, color:'#6B7280', fontWeight:900 }}>{progress.done}/{progress.total}</div>
            }
          </div>
          <div style={{ height:7, background:'#E5E7EB', borderRadius:8, overflow:'hidden' }}>
            <div style={{ width:`${progressPct}%`, height:'100%', background:'#006FDD', borderRadius:8 }}></div>
          </div>
        </div>

        <div className="setup-step-list">
          {steps.map((step, idx) => (
            <section key={step.key} className={`setup-step-row ${step.status === 'done' ? 'setup-step-row-done' : ''}`}>
              <div className="setup-step-number">{idx + 1}</div>
              <div className="setup-step-icon"><step.Icon size={17} strokeWidth={2}/></div>
              <div className="setup-step-copy">
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:4 }}>
                  <h2>{step.title}</h2>
                  <StepStatus status={step.status}/>
                </div>
                <p>{step.detail}</p>
                <div className="setup-step-result">
                  {step.result === null ? <SkeletonPill width={88} height={13}/> : step.result}
                </div>
              </div>
              <div className="setup-step-actions">
                {step.secondaryAction && (
                  <button onClick={() => startGuideAction(step.secondaryScreen, step.secondaryFocus)} className="setup-secondary-button">
                    {step.secondaryAction}
                  </button>
                )}
                <button onClick={() => startGuideAction(step.screen, step.focus)} className="setup-primary-button">
                  {step.action}<ArrowRight size={13}/>
                </button>
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};
